import { renderErrorAlert, renderWarningAlert } from "@whilecat/core/tasks/alerts.ts";
import { BaseTask } from "@whilecat/core/tasks/base-task.ts";
import {
    CS_PLACEHOLDER,
    type CompleteSentenceParams,
    CompleteSentenceParamsValidator,
} from "@whilecat/core/tasks/common/params/complete-sentence-params.js";
import { shuffleArray } from "@whilecat/core/utils/arrays.ts";
import { isPresent } from "@whilecat/core/utils/objects.ts";
import { isNotBlank } from "@whilecat/core/utils/string.ts";
import WhilecatStyle from "@whilecat/core/whilecat-style.js";
import { type PropertyValues, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

const EMPTY_BLOCK = "_____";

@customElement("complete-sentence-task")
export class CompleteSentenceTask extends BaseTask<CompleteSentenceParams> {
    static styles = [
        WhilecatStyle,
        super.styles,
        css`
          :host {
            width: 100%;
          }
          .hidden {
            opacity: 0;
            pointer-events: none;
            transform: scale(0.9);
          }
        `,
    ];

    @state() private selectedAnswers: Array<string> = [];
    @property({ type: Boolean }) private error: boolean = false;

    private paramsValidator = new CompleteSentenceParamsValidator();
    private specialMessage?: string | null = null;

    static html(taskId: string, data: CompleteSentenceParams, completed: boolean, repetitionMode: boolean) {
        return html`
          <complete-sentence-task
                  .taskId=${taskId}
                  .data=${data}
                  .completed="${completed}"
                  .repetitionMode="${repetitionMode}">
          </complete-sentence-task>`;
    }

    override firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        if (this.selectedAnswers.length === 0) {
            const placeholderCount = this.data!.sentence.split(CS_PLACEHOLDER).length - 1;
            this.selectedAnswers = Array(placeholderCount).fill(EMPTY_BLOCK);
        }
        this.data!.answers = shuffleArray(this.data!.answers);
    }

    public override showCorrectAnswer() {
        this.selectedAnswers = this.data!.rightAnswers;
        this.verify();
    }

    override verify() {
        const blankIndex = this.selectedAnswers.indexOf(EMPTY_BLOCK);
        if (blankIndex === -1) {
            this.error = !this.selectedAnswers.every((answer, index) => answer === this.data!.rightAnswers[index]);
            if (!this.error) {
                this.publishCompleted();
            }
        }
    }

    private renderSentenceParts() {
        const sentenceParts = this.data!.sentence.split(CS_PLACEHOLDER);
        return sentenceParts.map(
            (part, index) => html`
          ${part}
          ${
              index < sentenceParts.length - 1
                  ? html`
            <sl-button size="small" class="selected-answer"
                       @click="${() => this.unselectAnswer(index)}"
                       ?disabled="${this.completed || this.selectedAnswers[index] === EMPTY_BLOCK}">
              ${this.selectedAnswers[index]}
            </sl-button>
          `
                  : ""
          }
        `,
        );
    }

    private selectAnswer(answer: string) {
        const blankIndex = this.selectedAnswers.indexOf(EMPTY_BLOCK);
        if (isPresent(this.data!.specialAnswers) && isPresent(this.data!.specialAnswers![answer])) {
            this.selectedAnswers[blankIndex] = answer;
            this.specialMessage = this.data!.specialAnswers![answer];
        } else if (blankIndex !== -1) {
            this.specialMessage = "";
            this.selectedAnswers[blankIndex] = answer;
            this.verify();
        }
        this.requestUpdate();
    }

    private unselectAnswer(index: number) {
        this.selectedAnswers[index] = EMPTY_BLOCK;
        this.specialMessage = "";
        this.requestUpdate();
    }

    override validateData() {
        this.paramsValidator.validate(this.data!);
    }

    override render() {
        return html`
          ${this.data!.content}
          <div>
            <p>${this.renderSentenceParts()}</p>
            ${this.data!.answers.map(
                answer => html`
              <sl-button size="small"
                         class="answer ${this.selectedAnswers.includes(answer) ? "hidden" : ""}"
                         @click="${() => this.selectAnswer(answer)}"
                         ?disabled="${this.completed || this.selectedAnswers.indexOf(EMPTY_BLOCK) === -1}">
                ${answer}
              </sl-button>
            `,
            )}
          </div>
          <sl-divider></sl-divider>
          ${this.error ? renderErrorAlert() : nothing}
          ${isNotBlank(this.specialMessage) ? renderWarningAlert(this.specialMessage!, "") : nothing}
          </br>
        `;
    }
}
